// Player engine.
//
// Responsibilities:
//   - On boot, load the most recent cached bundle (default revision + any
//     scheduled revisions) and immediately start playing the right one for
//     the device's local time, so weak/dead networks do not blank the screen.
//   - Concurrently fetch the latest bundle from the Cloudflare Pages Function
//     `/api/player/heartbeat` (token-validated, fresh signed URLs) and
//     pre-cache assets for every revision in the bundle.
//   - On a periodic ticker, re-evaluate the schedule and swap to the
//     scheduled revision when its window opens — but only if its assets are
//     fully cached, otherwise keep playing the previous revision.
//   - Send heartbeats with selectedRevision + scheduleState telemetry.
//   - Subscribe to Supabase Realtime so admin-side publishes / schedule edits
//     sync immediately.
//   - Refresh signed URLs before they expire and enforce a soft cache
//     ceiling so a long-lived device does not drift into the platform quota.
//
// All time math is intentionally done in the device's LOCAL time zone. The
// server stores schedule windows as wall-clock strings; the player chooses
// based on its own clock.

import { supabase } from './supabase';
import {
  buildAssetCacheKey,
  buildLegacyCacheKey,
  fetchAndCache,
  getCachedResponse,
} from './asset-cache';
import {
  deleteAssetsForRevision,
  deleteRevision,
  getBundle,
  getMeta,
  getRevision,
  listAssetsForRevision,
  listRevisionsForScreen,
  putAsset,
  putRevision,
  setBundle,
  setMeta,
  type CachedAssetRow,
  type PlayerMeta,
} from './cache-db';
import { sendHeartbeat, type HeartbeatPayload, type HeartbeatSource } from './heartbeat-client';
import { getPlayEventsBuffer, type PlayEventsBuffer } from './play-events';
import { enforceCachePolicy } from './cache-policy';
import {
  BandwidthThrottle,
  DEFAULT_SYNC_INTERVAL_MS,
  effectiveConcurrency,
  effectiveMaxBytesPerSecond,
  effectiveSyncIntervalMs,
  shouldDownloadNow,
} from './bandwidth';
import type { ScreenBandwidthSettingsV1 } from '@/types/screen-settings';
import type { Manifest, ManifestImageVariant } from '@/types/manifest';
import {
  normalizeBundle,
  pickScheduledCandidate,
  scheduleMatches,
  type PlayerBundleV1,
  type ScheduleCandidate,
  type ScheduledRevisionEntry,
} from '@/types/schedule';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface PlayerStatus {
  meta: PlayerMeta | null;
  manifest: Manifest | null;
  /** Best-effort estimate from navigator.storage.estimate(). */
  storage?: { quota?: number; usage?: number };
  /** When syncing a new revision: 0..1 progress. */
  syncProgress?: number;
  /** Latest non-fatal error text. */
  error?: string;
  /** True once we have either failed token validation or hit a fatal RPC error. */
  fatal?: boolean;
  /** ID of the currently playing revision, scheduled or default. */
  activeRevisionId?: string | null;
  /**
   * `default` when playing the screen's default revision; `scheduled` when a
   * scheduled window is in effect.
   */
  source?: 'default' | 'scheduled' | 'fallback';
  /** Which transport was last used for heartbeat. */
  heartbeatSource?: HeartbeatSource;
  /** Bundle version reported by the server on the last sync. */
  bundleVersion?: string;
  /** Server-reported expiry for the URLs in the current bundle. */
  urlExpiresAt?: number;
  /** True when the server capped the scheduled revisions returned in the bundle. */
  bundleTruncated?: boolean;
  /** Total number of schedule_enabled revisions on the server (pre-cap). */
  scheduledCount?: number;
  /** Number of revisions evicted by the most recent cache pass. */
  lastEvictedCount?: number;
  /** Bandwidth settings currently in effect (after server -> client merge). */
  bandwidth?: ScreenBandwidthSettingsV1;
  /** True when quiet hours are paused new asset downloads at this moment. */
  quietHoursActive?: boolean;
}

export interface PlayerEngineOptions {
  screenId: string;
  /** Player access token. Required: the engine refuses to call any RPC without it. */
  playerToken: string;
  playerName?: string;
  onStatusChange: (status: PlayerStatus) => void;
  onManifestReady: (manifest: Manifest, revisionId: string) => void;
  /**
   * Optional clock injection for tests. Defaults to () => new Date(). The
   * engine never calls `Date.now()` directly so all schedule decisions use
   * this clock.
   */
  now?: () => Date;
  /** Override the cache-bytes ceiling. Defaults to 4 GiB. */
  maxCacheBytes?: number;
  /**
   * Initial bandwidth settings. Optional — the server typically supplies
   * these via the bundle, but callers (and tests) may pre-seed them so the
   * engine respects quiet hours / sync interval before the first sync.
   */
  bandwidth?: ScreenBandwidthSettingsV1;
}

const HEARTBEAT_INTERVAL_MS = 30_000;
const SCHEDULE_TICK_MS = 30_000;
const REALTIME_SYNC_DEBOUNCE_MS = 750;

const FATAL_TOKEN_ERRORS = new Set(['missing_token', 'invalid_token', 'revoked']);

export class PlayerEngine {
  private opts: PlayerEngineOptions;
  private aborter = new AbortController();
  private heartbeatTimer: number | null = null;
  private syncTimer: number | null = null;
  private scheduleTimer: number | null = null;
  private currentRevisionId: string | null = null;
  private currentBundle: PlayerBundleV1 | null = null;
  private fullyCached = new Set<string>();
  private status: PlayerStatus = { meta: null, manifest: null };
  private realtimeChannel: RealtimeChannel | null = null;
  private realtimeDebounceTimer: number | null = null;
  private stopped = false;
  private now: () => Date;
  private lastBundleVersion: string | null = null;
  private bandwidthSettings: ScreenBandwidthSettingsV1 | undefined;
  private throttle: BandwidthThrottle;
  /**
   * Currently armed sync-tick interval. Tracked so we only re-arm the timer
   * when the effective interval actually changes.
   */
  private currentSyncIntervalMs: number = DEFAULT_SYNC_INTERVAL_MS;
  private playEventsBuffer: PlayEventsBuffer;

  constructor(opts: PlayerEngineOptions) {
    this.opts = opts;
    this.now = opts.now ?? (() => new Date());
    this.bandwidthSettings = opts.bandwidth;
    this.throttle = new BandwidthThrottle({
      maxConcurrent: effectiveConcurrency(this.bandwidthSettings),
      maxBytesPerSecond: effectiveMaxBytesPerSecond(this.bandwidthSettings),
    });
    this.currentSyncIntervalMs = effectiveSyncIntervalMs(this.bandwidthSettings);
    this.playEventsBuffer = getPlayEventsBuffer(opts.screenId);
  }

  /**
   * Expose the screen-scoped Proof-of-Play buffer so the player UI can
   * record per-item start/end events. Engine stamps schedule/bundle context
   * onto the buffer on every activation + sync.
   */
  getPlayEventsBuffer(): PlayEventsBuffer {
    return this.playEventsBuffer;
  }

  async start() {
    await requestPersistence();

    if (!this.opts.playerToken) {
      this.status = { ...this.status, error: 'missing_token', fatal: true };
      this.emit();
      return;
    }

    // Restore the most recent bundle and the set of fully-cached revisions
    // from IndexedDB so we can boot offline.
    const [bundle, cached] = await Promise.all([
      getBundle(this.opts.screenId),
      listRevisionsForScreen(this.opts.screenId),
    ]);
    this.currentBundle = bundle;
    this.lastBundleVersion = bundle?.bundleVersion ?? null;
    for (const r of cached) if (r.fullyCached) this.fullyCached.add(r.revisionId);

    // Pick a starting revision based on the cached bundle + local time.
    if (bundle) {
      await this.evaluateSchedule({ initial: true });
    } else {
      // Legacy boot path: no bundle persisted yet. Fall back to the most
      // recent fully-cached revision (matches pre-scheduling behaviour).
      const meta = await getMeta(this.opts.screenId);
      if (meta?.activeRevisionId) {
        const rev = await getRevision(meta.activeRevisionId);
        if (rev) {
          this.activate(rev.manifest as Manifest, rev.revisionId, 'default');
        }
      }
    }

    // Pick up settings persisted from a previous boot so quiet hours /
    // sync-interval apply on the first tick, before we hear from the server.
    if (bundle?.bandwidth) {
      this.applyBandwidthSettings(bundle.bandwidth);
    }

    void this.sync();
    this.armSyncTimer();
    this.heartbeatTimer = window.setInterval(
      () => void this.heartbeat('alive'),
      HEARTBEAT_INTERVAL_MS,
    );
    // Re-evaluate the schedule every 30s so window transitions take effect
    // even when the network is offline / Realtime is disconnected.
    this.scheduleTimer = window.setInterval(
      () => void this.evaluateSchedule(),
      SCHEDULE_TICK_MS,
    );

    this.subscribeRealtime();
  }

  stop() {
    this.stopped = true;
    this.aborter.abort();
    if (this.heartbeatTimer) window.clearInterval(this.heartbeatTimer);
    if (this.syncTimer) window.clearInterval(this.syncTimer);
    if (this.scheduleTimer) window.clearInterval(this.scheduleTimer);
    if (this.realtimeDebounceTimer) window.clearTimeout(this.realtimeDebounceTimer);
    if (this.realtimeChannel) {
      void supabase.removeChannel(this.realtimeChannel);
      this.realtimeChannel = null;
    }
  }

  /**
   * Convert a manifest URL into a usable URL for <img>/<video>. Prefers the
   * cached blob URL when available; falls back to the signed remote URL.
   */
  async resolveAssetUrl(
    assetId: string,
    revisionId: string,
    remoteUrl: string,
    contentHash?: string | null,
  ): Promise<string> {
    const candidates: string[] = [];
    if (contentHash) candidates.push(buildAssetCacheKey(contentHash));
    candidates.push(buildLegacyCacheKey(assetId, revisionId));
    for (const key of candidates) {
      const cached = await getCachedResponse(key);
      if (cached) {
        const blob = await cached.blob();
        return URL.createObjectURL(blob);
      }
    }
    return remoteUrl;
  }

  private emit() {
    this.opts.onStatusChange({ ...this.status });
  }

  /**
   * (Re-)arm the periodic sync timer with the currently effective interval.
   * Called on start, and again whenever new bandwidth settings flip the
   * interval (e.g. an admin saved a new sync interval).
   */
  private armSyncTimer() {
    if (this.syncTimer) window.clearInterval(this.syncTimer);
    this.syncTimer = window.setInterval(
      () => void this.sync(),
      this.currentSyncIntervalMs,
    );
  }

  /**
   * Apply a (possibly new) bandwidth settings document. Re-arms the sync
   * timer when the effective interval changes and updates the throttle so
   * concurrency / rate caps take effect on the next download.
   */
  private applyBandwidthSettings(next: ScreenBandwidthSettingsV1 | undefined) {
    this.bandwidthSettings = next;
    const intervalMs = effectiveSyncIntervalMs(next);
    if (intervalMs !== this.currentSyncIntervalMs) {
      this.currentSyncIntervalMs = intervalMs;
      if (this.syncTimer) this.armSyncTimer();
    }
    this.throttle.update({
      maxConcurrent: effectiveConcurrency(next),
      maxBytesPerSecond: effectiveMaxBytesPerSecond(next),
    });
    this.status = {
      ...this.status,
      bandwidth: next,
    };
  }

  private subscribeRealtime() {
    try {
      const channel = supabase.channel(`signage-screen:${this.opts.screenId}`);
      channel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'screens',
          filter: `id=eq.${this.opts.screenId}`,
        },
        () => this.scheduleRealtimeSync(),
      );
      channel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'screen_revisions',
          filter: `screen_id=eq.${this.opts.screenId}`,
        },
        () => this.scheduleRealtimeSync(),
      );
      channel.subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          // Realtime is best-effort; polling fallback continues.
        }
      });
      this.realtimeChannel = channel;
    } catch {
      // If realtime fails to set up, polling still works.
    }
  }

  private scheduleRealtimeSync() {
    if (this.stopped) return;
    if (this.realtimeDebounceTimer) window.clearTimeout(this.realtimeDebounceTimer);
    this.realtimeDebounceTimer = window.setTimeout(() => {
      void this.sync();
    }, REALTIME_SYNC_DEBOUNCE_MS);
  }

  private async buildHeartbeatPayload(cacheStatus: string): Promise<HeartbeatPayload> {
    const scheduleState = this.computeScheduleState();
    // Stamp the latest context onto the buffer so any subsequent
    // recordEnd() picks up the current schedule/bundle for tagging.
    this.playEventsBuffer.setContext({
      scheduleState,
      bundleVersion: this.lastBundleVersion,
    });
    const playEvents = await this.playEventsBuffer.drain().catch(() => []);
    const dropped = this.playEventsBuffer.consumeDroppedCount();
    return {
      screenId: this.opts.screenId,
      playerToken: this.opts.playerToken,
      playerName: this.opts.playerName ?? null,
      cachedRevision: this.currentRevisionId,
      cacheStatus,
      userAgent: navigator.userAgent,
      storageEstimate: this.status.storage ?? null,
      lastError: dropped > 0
        ? `play_events_dropped:${dropped}`
        : this.status.error ?? null,
      selectedRevision: this.currentRevisionId,
      scheduleState,
      bundleVersion: this.lastBundleVersion,
      playEvents: playEvents.length > 0 ? playEvents : undefined,
    };
  }

  private computeScheduleState(): string {
    const src = this.status.source;
    if (!src) return 'none';
    if (src === 'scheduled' && this.currentBundle && this.currentRevisionId) {
      const entry = this.currentBundle.scheduled.find(
        (s) => s.revisionId === this.currentRevisionId,
      );
      if (entry) return `scheduled:${entry.priority}`;
    }
    return src;
  }

  private async sync() {
    if (this.status.fatal) return;
    try {
      const storage = await getStorageEstimate();
      this.status = { ...this.status, storage };

      const remote = await sendHeartbeat(await this.buildHeartbeatPayload('syncing'));
      this.applyPlayerSettings(remote);
      this.ackPlayEvents(remote);
      if (remote.error && FATAL_TOKEN_ERRORS.has(remote.error)) {
        this.status = {
          ...this.status,
          error: remote.error,
          fatal: true,
          heartbeatSource: remote.source,
        };
        this.emit();
        return;
      }

      // The Pages Function is unreachable (and the build is in strict
      // production transport mode). Keep playing whatever is cached and
      // surface a non-fatal diagnostic so admins can see the problem.
      if (remote.error === 'service_unavailable') {
        this.status = {
          ...this.status,
          error: 'service_unavailable',
          heartbeatSource: remote.source,
        };
        this.emit();
        return;
      }

      const bundle = remote.bundle ?? this.synthesizeBundle(remote.activeRevision, remote.manifest);
      if (!bundle || (!bundle.defaultManifest && bundle.scheduled.length === 0)) {
        // Server has no published manifest. Keep playing whatever we have.
        // If a v2 manifest was returned without a signed URL by the dev
        // RPC fallback, surface that too rather than silently looking
        // healthy.
        if (remote.unsignedV2) {
          this.status = {
            ...this.status,
            error: 'unsigned_v2_manifest',
            heartbeatSource: remote.source,
          };
        }
        await this.heartbeat('no_active_revision');
        this.emit();
        return;
      }

      this.currentBundle = bundle;
      this.lastBundleVersion = bundle.bundleVersion ?? this.lastBundleVersion;
      await setBundle(this.opts.screenId, bundle);

      // Pick up bandwidth settings the server attached to the bundle. This
      // re-arms the sync timer / throttle BEFORE we kick off potentially
      // expensive downloads, so policy changes apply this tick.
      this.applyBandwidthSettings(bundle.bandwidth);

      // Cache every revision in the bundle (default + scheduled). Skip ones
      // already fully cached. We push progress while caching so the admin
      // diagnostics tab sees progress for big windows.
      await this.heartbeat('syncing');
      await this.cacheBundle(bundle);

      await this.evaluateSchedule();
      await this.gcUnreferencedRevisions(bundle);
      await this.enforceQuota(bundle);

      this.status = {
        ...this.status,
        heartbeatSource: remote.source,
        bundleVersion: bundle.bundleVersion ?? this.status.bundleVersion,
        urlExpiresAt: bundle.urlExpiresAt ?? this.status.urlExpiresAt,
        bundleTruncated: bundle.truncated ?? false,
        scheduledCount: bundle.scheduledCount,
      };
      await this.heartbeat(this.status.error ? `error:${this.status.error}` : 'up_to_date');
      this.emit();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'sync_failed';
      this.status = { ...this.status, error: msg };
      await this.heartbeat(`error:${msg}`);
      this.emit();
    }
  }

  /**
   * Old responses (pre-scheduling) only carry `manifest` + `activeRevision`.
   * Wrap them into a synthetic single-default bundle so the rest of the
   * engine has a uniform code path.
   */
  private synthesizeBundle(
    activeRevision: string | null,
    manifest: Manifest | null,
  ): PlayerBundleV1 | null {
    if (!manifest || !activeRevision) return null;
    return {
      schemaVersion: 1,
      defaultRevisionId: activeRevision,
      defaultManifest: manifest,
      scheduled: [],
    };
  }

  /**
   * Re-evaluate which revision should be playing right now. Picks the
   * highest-priority scheduled revision whose window matches the current
   * local time AND whose assets are fully cached. Falls back to the default
   * revision when nothing matches; falls back to whatever we're currently
   * playing if the default isn't cached yet either.
   */
  private async evaluateSchedule(opts: { initial?: boolean } = {}): Promise<void> {
    const bundle = this.currentBundle;
    if (!bundle) return;

    const now = this.now();
    const target = chooseRevision(bundle, now, this.fullyCached);

    if (!target) {
      if (opts.initial) {
        // Best-effort offline boot: play whatever is cached, even partially.
        const fallback = await pickAnyCachedRevision(this.opts.screenId);
        if (fallback) {
          this.activate(fallback.manifest as Manifest, fallback.revisionId, 'fallback');
        }
      }
      return;
    }

    if (target.revisionId === this.currentRevisionId) return;

    // Load the manifest we cached for this revision.
    const row = await getRevision(target.revisionId);
    const manifest = (row?.manifest ?? target.manifest) as Manifest | undefined;
    if (!manifest) return;

    this.activate(manifest, target.revisionId, target.source);
  }

  /**
   * Switch to the given (manifest, revisionId) atomically and notify the
   * outer page. Also persists the screen-level meta so a cold boot remembers
   * the last-played revision.
   */
  private activate(manifest: Manifest, revisionId: string, source: PlayerStatus['source']) {
    this.currentRevisionId = revisionId;
    const meta: PlayerMeta = {
      screenId: this.opts.screenId,
      activeRevisionId: revisionId,
      cacheStatus: this.fullyCached.has(revisionId) ? 'ready' : 'syncing',
      lastSyncAt: this.now().getTime(),
    };
    void setMeta(meta);
    this.status = {
      ...this.status,
      meta,
      manifest,
      activeRevisionId: revisionId,
      source,
    };
    // Stamp the buffer so play events recorded after this activation carry
    // the new schedule/bundle context.
    this.playEventsBuffer.setContext({
      scheduleState: this.computeScheduleState(),
      bundleVersion: this.lastBundleVersion,
    });
    this.opts.onManifestReady(manifest, revisionId);
    this.emit();
  }

  private async heartbeat(status: string) {
    if (this.status.fatal) return;
    try {
      const storage = await getStorageEstimate();
      this.status = { ...this.status, storage };
      const result = await sendHeartbeat(await this.buildHeartbeatPayload(status));
      this.applyPlayerSettings(result);
      this.ackPlayEvents(result);
      this.status = { ...this.status, heartbeatSource: result.source };
      if (result.error && FATAL_TOKEN_ERRORS.has(result.error)) {
        this.status = { ...this.status, error: result.error, fatal: true };
        this.emit();
      }
    } catch {
      // ignore - network may be down
    }
  }

  /**
   * Apply per-player runtime settings returned by the heartbeat. The only
   * setting today is the Proof-of-Play toggle; an absent `playerSettings`
   * (older edge response or the legacy RPC) leaves the previous value in
   * place, defaulting to enabled at boot.
   */
  private applyPlayerSettings(result: {
    playerSettings?: { proofOfPlayEnabled: boolean };
  }): void {
    if (!result.playerSettings) return;
    this.playEventsBuffer.setEnabled(result.playerSettings.proofOfPlayEnabled);
  }

  /**
   * Remove play events the server confirmed (accepted) or rejected (validation
   * failure / dedupe noop) from the local IndexedDB buffer. Rejected ids are
   * also dropped to prevent a poison-pill row from looping forever.
   */
  private ackPlayEvents(result: {
    acceptedPlayEventIds?: string[];
    rejectedPlayEventIds?: string[];
  }): void {
    const ids: string[] = [];
    if (result.acceptedPlayEventIds) ids.push(...result.acceptedPlayEventIds);
    if (result.rejectedPlayEventIds) ids.push(...result.rejectedPlayEventIds);
    if (ids.length === 0) return;
    void this.playEventsBuffer.ack(ids);
  }

  /**
   * Cache the bundle's revisions in priority order:
   *
   *   1. The currently-playing revision (so a flicker recovers fastest).
   *   2. The default revision (the screen-wide fallback).
   *   3. Any scheduled revision whose window matches NOW.
   *   4. The remaining scheduled revisions (pre-cached for upcoming windows).
   *
   * Each is cached independently so a partial failure on one window doesn't
   * block the rest.
   */
  private async cacheBundle(bundle: PlayerBundleV1): Promise<void> {
    // Decide once for the whole bundle pass whether quiet hours block new
    // downloads. The currently-playing revision is always cached (so the
    // screen never blanks); only fanning out to *other* revisions is
    // skipped during quiet hours.
    const downloadsAllowed = shouldDownloadNow(this.bandwidthSettings, this.now());
    this.status = {
      ...this.status,
      quietHoursActive: !downloadsAllowed,
    };

    const seen = new Set<string>();
    const targets: { revisionId: string; revision: number; manifest: Manifest }[] = [];
    const push = (revisionId: string, revision: number, manifest: Manifest) => {
      if (seen.has(revisionId)) return;
      seen.add(revisionId);
      targets.push({ revisionId, revision, manifest });
    };

    // 1. Current revision, if it's referenced by the bundle.
    if (this.currentRevisionId) {
      if (
        bundle.defaultRevisionId === this.currentRevisionId &&
        bundle.defaultManifest
      ) {
        const m = bundle.defaultManifest as Manifest;
        push(this.currentRevisionId, m.revision, m);
      } else {
        const cur = bundle.scheduled.find((s) => s.revisionId === this.currentRevisionId);
        if (cur) push(cur.revisionId, cur.revision, cur.manifest as Manifest);
      }
    }

    // 2. Default.
    if (bundle.defaultRevisionId && bundle.defaultManifest) {
      const m = bundle.defaultManifest as Manifest;
      push(bundle.defaultRevisionId, m.revision, m);
    }

    // 3. Currently-matching scheduled revisions (about to become current).
    const now = this.now();
    const matchingNow = bundle.scheduled.filter((s) => scheduleMatches(s.schedule, now));
    for (const s of matchingNow) {
      push(s.revisionId, s.revision, s.manifest as Manifest);
    }

    // 4. Remaining scheduled, in the server's priority order.
    for (const s of bundle.scheduled) {
      push(s.revisionId, s.revision, s.manifest as Manifest);
    }

    let firstError: string | undefined;
    // On a cold boot the screen has nothing playing yet. We must download the
    // default revision once even during quiet hours, otherwise the screen
    // would stay blank indefinitely. Once anything is cached the gate kicks
    // in normally.
    const allowFirstFill = !this.currentRevisionId;
    let firstFillUsed = false;
    for (const t of targets) {
      const isPlaying = t.revisionId === this.currentRevisionId;
      const isCached = this.fullyCached.has(t.revisionId);
      const isFirstFill = allowFirstFill && !firstFillUsed;
      if (!downloadsAllowed && !isPlaying && !isCached && !isFirstFill) {
        if (!firstError) firstError = 'quiet_hours';
        continue;
      }
      const ok = await this.cacheRevision(t.manifest, t.revisionId);
      if (ok) {
        this.fullyCached.add(t.revisionId);
        if (isFirstFill) firstFillUsed = true;
      } else if (!firstError) {
        firstError = 'partial_cache';
      }
    }
    this.status = {
      ...this.status,
      error: firstError,
    };
  }

  private async cacheRevision(manifest: Manifest, revisionId: string): Promise<boolean> {
    // Persist the manifest first so an interrupted run can resume.
    await putRevision({
      revisionId,
      screenId: this.opts.screenId,
      revision: manifest.revision,
      manifest: manifest as unknown,
      cachedAt: this.now().getTime(),
      fullyCached: false,
    });

    const required = collectRequiredAssets(manifest, revisionId);
    const totals = required.length;
    let done = 0;
    let firstError: string | undefined;

    for (const item of required) {
      // Skip items that don't have a usable URL. These can occur if signing
      // failed server-side; the player will keep playing the cached copy if
      // it has one and try again on the next sync.
      if (!item.url) {
        const existing = await getCachedResponse(item.cacheKey);
        if (!existing && !firstError) firstError = 'missing_url';
        done += 1;
        this.status = { ...this.status, syncProgress: done / Math.max(1, totals) };
        this.emit();
        continue;
      }
      const existing = await getCachedResponse(item.cacheKey);
      if (existing) {
        await putAsset({
          cacheKey: item.cacheKey,
          assetId: item.assetId,
          revisionId,
          url: item.url,
          status: 'cached',
          bytes: existing.headers.get('content-length')
            ? Number(existing.headers.get('content-length'))
            : 0,
          cachedAt: this.now().getTime(),
        });
        done += 1;
        this.status = { ...this.status, syncProgress: done / Math.max(1, totals) };
        this.emit();
        continue;
      }

      await putAsset({
        cacheKey: item.cacheKey,
        assetId: item.assetId,
        revisionId,
        url: item.url,
        status: 'pending',
        bytes: 0,
      });
      const result = await fetchAndCache(
        item.cacheKey,
        item.url,
        this.aborter.signal,
        this.throttle,
      );
      const row: CachedAssetRow = {
        cacheKey: item.cacheKey,
        assetId: item.assetId,
        revisionId,
        url: item.url,
        status: result.ok ? 'cached' : 'failed',
        bytes: result.bytes,
        lastError: result.error,
        cachedAt: result.ok ? this.now().getTime() : undefined,
      };
      await putAsset(row);
      done += 1;
      if (!result.ok && !firstError) firstError = result.error;
      this.status = { ...this.status, syncProgress: done / Math.max(1, totals) };
      this.emit();
    }

    if (firstError) return false;

    await putRevision({
      revisionId,
      screenId: this.opts.screenId,
      revision: manifest.revision,
      manifest: manifest as unknown,
      cachedAt: this.now().getTime(),
      fullyCached: true,
    });
    return true;
  }

  /**
   * Drop cached revisions (and their assets) that are no longer referenced
   * by the bundle. Asset cache keys still referenced by another kept
   * revision are preserved (deduplication via content-hash keys).
   */
  private async gcUnreferencedRevisions(bundle: PlayerBundleV1) {
    const keep = new Set<string>();
    if (bundle.defaultRevisionId) keep.add(bundle.defaultRevisionId);
    for (const s of bundle.scheduled) keep.add(s.revisionId);

    const all = await listRevisionsForScreen(this.opts.screenId);
    const toDelete = all.filter((r) => !keep.has(r.revisionId));
    if (toDelete.length === 0) return;

    // Build the set of asset cache keys still in use by kept revisions.
    const stillNeeded = new Set<string>();
    for (const id of keep) {
      const assets = await listAssetsForRevision(id);
      assets.forEach((a) => stillNeeded.add(a.cacheKey));
    }

    for (const rev of toDelete) {
      const assets = await listAssetsForRevision(rev.revisionId);
      const cacheKeys = assets
        .map((a) => a.cacheKey)
        .filter((k) => !stillNeeded.has(k));
      try {
        const cache = await caches.open('signage-media-v1');
        await Promise.all(cacheKeys.map((k) => cache.delete(k)));
      } catch {
        /* ignore */
      }
      await deleteAssetsForRevision(rev.revisionId);
      await deleteRevision(rev.revisionId);
      this.fullyCached.delete(rev.revisionId);
    }
  }

  /**
   * Protect the smallest viable set from eviction:
   *   1. The currently playing revision (never evict).
   *   2. The default revision (the fallback when no schedule matches).
   *   3. Any scheduled revision whose window matches NOW.
   *
   * Far-future scheduled revisions are pre-cached during sync but become
   * eligible for eviction under storage pressure. They will be re-fetched
   * before their window opens because the engine periodically resyncs the
   * bundle and re-caches everything in it.
   */
  private async enforceQuota(bundle: PlayerBundleV1) {
    const protectedIds = new Set<string>();
    if (this.currentRevisionId) protectedIds.add(this.currentRevisionId);
    if (bundle.defaultRevisionId) protectedIds.add(bundle.defaultRevisionId);
    const now = this.now();
    for (const s of bundle.scheduled) {
      if (scheduleMatches(s.schedule, now)) {
        protectedIds.add(s.revisionId);
      }
    }

    const result = await enforceCachePolicy({
      screenId: this.opts.screenId,
      protectedRevisionIds: protectedIds,
      maxBytes: this.opts.maxCacheBytes,
      storage: this.status.storage,
    });
    if (result.evictedRevisions.length > 0) {
      for (const id of result.evictedRevisions) this.fullyCached.delete(id);
    }
    this.status = {
      ...this.status,
      lastEvictedCount: result.evictedRevisions.length,
    };
  }
}

interface RequiredAsset {
  cacheKey: string;
  assetId: string;
  url?: string;
}

function collectRequiredAssets(manifest: Manifest, revisionId: string): RequiredAsset[] {
  const out: RequiredAsset[] = [];
  for (const it of manifest.playlist) {
    // Webpages and weather widgets are live; they have no bytes to fetch,
    // so skip them so they don't block the revision from being marked
    // fullyCached.
    if (it.kind === 'webpage' || it.kind === 'weather') continue;
    out.push({
      cacheKey: it.contentHash
        ? buildAssetCacheKey(it.contentHash)
        : buildLegacyCacheKey(it.assetId, revisionId),
      assetId: it.assetId,
      url: it.url,
    });
  }
  for (const it of manifest.audioPlaylist ?? []) {
    out.push({
      cacheKey: it.contentHash
        ? buildAssetCacheKey(it.contentHash)
        : buildLegacyCacheKey(it.assetId, revisionId),
      assetId: it.assetId,
      url: it.url,
    });
  }
  for (const [k, v] of Object.entries(manifest.overlay.variants ?? {})) {
    if (!v || v.kind !== 'image') continue;
    const variant = v as ManifestImageVariant;
    const fakeId = variant.mediaAssetId ?? `overlay:${k}`;
    out.push({
      cacheKey: variant.contentHash
        ? buildAssetCacheKey(variant.contentHash)
        : buildLegacyCacheKey(fakeId, revisionId),
      assetId: fakeId,
      url: variant.url,
    });
  }
  return out;
}

async function getStorageEstimate(): Promise<{ quota?: number; usage?: number } | undefined> {
  try {
    if (navigator.storage && 'estimate' in navigator.storage) {
      const est = await navigator.storage.estimate();
      return { quota: est.quota, usage: est.usage };
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

async function requestPersistence() {
  try {
    if (navigator.storage && 'persist' in navigator.storage) {
      await navigator.storage.persist();
    }
  } catch {
    /* persistence is best-effort */
  }
}

/**
 * Resolve the cache key for a manifest playlist item, preferring the stable
 * content-hash key when available.
 */
export function playlistItemCacheKey(
  item: { assetId: string; contentHash?: string | null },
  revisionId: string,
): string {
  return item.contentHash
    ? buildAssetCacheKey(item.contentHash)
    : buildLegacyCacheKey(item.assetId, revisionId);
}

/**
 * Resolve the cache key for an overlay image variant within a manifest.
 */
export function overlayVariantCacheKey(
  variant: { contentHash?: string | null },
  variantKey: string,
  revisionId: string,
): string {
  return variant.contentHash
    ? buildAssetCacheKey(variant.contentHash)
    : buildLegacyCacheKey(`overlay:${variantKey}`, revisionId);
}

// Re-export for tests / external normalisation.
export { normalizeBundle };

// ---------------------------------------------------------------------------
// Pure schedule selection. Exported so the unit/e2e tests can drive it
// without spinning up a real engine.
// ---------------------------------------------------------------------------

export interface ChosenRevision {
  revisionId: string;
  manifest: Manifest;
  source: 'default' | 'scheduled';
}

/**
 * Decide which revision the player should be playing right now, given the
 * bundle, the local clock, and the set of revision IDs that are fully
 * cached. A scheduled revision only wins if its window matches AND its
 * assets are cached. Otherwise we fall back to the default revision (also
 * only if cached), and otherwise return null.
 */
export function chooseRevision(
  bundle: PlayerBundleV1,
  now: Date,
  cached: ReadonlySet<string>,
): ChosenRevision | null {
  // Build candidate list from scheduled entries, filtering to ones we have
  // fully cached so we never switch to something that would blank the screen.
  const cachedScheduled: ScheduleCandidate<ScheduledRevisionEntry>[] = bundle.scheduled
    .filter((s) => cached.has(s.revisionId))
    .map((s) => ({
      revisionId: s.revisionId,
      schedule: s.schedule,
      priority: s.priority,
      revision: s.revision,
      publishedAt: s.publishedAt,
      value: s,
    }));

  const winner = pickScheduledCandidate(cachedScheduled, now);
  if (winner) {
    return {
      revisionId: winner.revisionId,
      manifest: winner.value.manifest as Manifest,
      source: 'scheduled',
    };
  }

  if (
    bundle.defaultRevisionId &&
    bundle.defaultManifest &&
    cached.has(bundle.defaultRevisionId)
  ) {
    return {
      revisionId: bundle.defaultRevisionId,
      manifest: bundle.defaultManifest as Manifest,
      source: 'default',
    };
  }
  return null;
}

/**
 * Last-resort fallback used during cold offline boot: any revision we have
 * cached at all, even partially. Avoids blanking the screen when the bundle
 * is unknown.
 */
async function pickAnyCachedRevision(
  screenId: string,
): Promise<{ revisionId: string; manifest: unknown } | null> {
  const all = await listRevisionsForScreen(screenId);
  if (all.length === 0) return null;
  const sorted = [...all].sort((a, b) => b.revision - a.revision);
  return sorted[0] ?? null;
}
